import type { LocalOverrides } from "~/store/productionScheduleSlice";
import type { ProducedLocationShare } from "~produce/production/schedule/helpers/producedLocationSummary";
import type { JobStatus } from "~produce/production/schedule/import/helpers/types";

import {
	JobExecutionStatus,
	JobPostingStatus,
	MissingDataCode,
	type ProductionLotStatus
} from "~/gql/graphqlBase.generated";

// wsapi GridRegistry key of the Production Schedule grid.
export const PRODUCTION_SCHEDULE_GRID_ID = "productionSchedule";

export interface ProductionScheduleSsrmRow {
	categoryName?: string | null;
	color?: string | null;
	countedAt?: string | null;
	customerGroupId?: string | null;
	customerGroupName?: string | null;
	customerId?: string | null;
	customerName?: string | null;
	eaches?: number | null;
	eachesPerContainer?: number | null;
	effectivePlantsPerPot?: number | null;
	genus?: string | null;
	hasChildren?: boolean;
	isGrowWeeksOverridden?: boolean;
	isPlantsPerPotOverridden?: boolean;
	isPreFinished?: boolean;
	isQuantityOverridden?: boolean;
	isYieldOverridden?: boolean;
	jobCode?: string | null;
	jobExecutionStatus?: JobExecutionStatus | null;
	jobNumber?: number | null;
	locationId?: string | null;
	locationName?: string | null;
	lotCode?: string | null;
	lotQuantity?: number | null;
	lotStatus?: ProductionLotStatus | null;
	noMaterialEvents?: boolean | null;
	noRecipeAtSite?: boolean | null;
	noTaskEvents?: boolean | null;
	orderIds?: string[] | null;
	orderNumbers?: string[] | null;
	originalCatalog?: string | null;
	originalWeekReadyIsoWeek?: number | null;
	originalWeekReadyIsoYear?: number | null;
	originalWeekStartIsoWeek?: number | null;
	parentLocationName?: string | null;
	parentProductionJobId?: string | null;
	postedQuantity?: number | null;
	postingStatus?: JobPostingStatus | null;
	producedEaches?: number | null;
	producedLocationId?: string | null;
	producedLocations?: ProducedLocationShare[] | null;
	producedQuantity?: number | null;
	productionJobId: string;
	productionLotId: string;
	productionPlanName?: string | null;
	productionRecipeId?: string | null;
	productionRecipeVariationId?: string | null;
	productionTime?: number | null;
	quantity?: number | null;
	requestedEaches?: number | null;
	series?: string | null;
	siteName?: string | null;
	tagCode?: string | null;
	tagSku?: string | null;
	tagUpc?: string | null;
	unitMismatch?: boolean | null;
	uomCode?: string | null;
	uomLabel?: string | null;
	weekReadyDate?: string | null;
	weekReadyIsoWeek?: number | null;
	weekReadyIsoYear?: number | null;
	weekStartDate?: string | null;
	weekStartIsoWeek?: number | null;
	weekStartIsoYear?: number | null;
	yield?: number | null;
}

export const isPreFinishedLotRow = (row: ProductionScheduleSsrmRow | undefined): boolean =>
	Boolean(row?.isPreFinished) && !row?.parentProductionJobId;

export const overlayLocalOverrides = (
	rows: ProductionScheduleSsrmRow[],
	localOverrides: Record<string, LocalOverrides>
): ProductionScheduleSsrmRow[] =>
	rows.map((row) => {
		const override = localOverrides[row.productionJobId];
		if (!override) return { ...row };
		return {
			...row,
			...(override.customerGroupId !== undefined ? { customerGroupId: override.customerGroupId } : {}),
			...(override.customerGroupName !== undefined ? { customerGroupName: override.customerGroupName } : {}),
			...(override.customerName !== undefined ? { customerName: override.customerName } : {}),
			...(override.locationName != null ? { locationName: override.locationName } : {}),
			...(override.parentLocationName !== undefined ? { parentLocationName: override.parentLocationName } : {}),
			...(override.siteName != null ? { siteName: override.siteName } : {}),
			...(override.productionPlanName != null ? { productionPlanName: override.productionPlanName } : {}),
			...(override.quantity != null ? { quantity: override.quantity } : {}),
			...(override.yield != null ? { yield: override.yield } : {}),
			...(override.effectivePlantsPerPot != null ? { effectivePlantsPerPot: override.effectivePlantsPerPot } : {}),
			...(override.isQuantityOverridden != null ? { isQuantityOverridden: override.isQuantityOverridden } : {}),
			...(override.isYieldOverridden != null ? { isYieldOverridden: override.isYieldOverridden } : {}),
			...(override.isPlantsPerPotOverridden != null
				? { isPlantsPerPotOverridden: override.isPlantsPerPotOverridden }
				: {})
		};
	});

const JOB_EXECUTION_STATUS_TO_JOB_STATUS: Record<string, JobStatus> = {
	EXECUTED: "Completed",
	EXECUTING: "In Progress",
	PENDING: "Scheduled"
};

export const deriveJobStatus = (jobExecutionStatus: JobExecutionStatus | null | undefined): JobStatus =>
	(jobExecutionStatus && JOB_EXECUTION_STATUS_TO_JOB_STATUS[jobExecutionStatus]) ?? "Scheduled";

export const deriveProducedJobStatus = ({
	jobExecutionStatus,
	parentProductionJobId,
	postingStatus
}: Pick<ProductionScheduleSsrmRow, "jobExecutionStatus" | "parentProductionJobId" | "postingStatus">): JobStatus => {
	if (parentProductionJobId) return deriveJobStatus(jobExecutionStatus);
	if (postingStatus === JobPostingStatus.Closed) return "Completed";
	const isPlanted =
		jobExecutionStatus === JobExecutionStatus.Executing || jobExecutionStatus === JobExecutionStatus.Executed;
	return postingStatus === JobPostingStatus.PartiallyPosted || isPlanted ? "In Progress" : "Scheduled";
};

export const resolveJobStatus = (
	job: Pick<ProductionScheduleSsrmRow, "jobExecutionStatus" | "parentProductionJobId" | "postingStatus">,
	producedFulfillEnabled: boolean
): JobStatus => (producedFulfillEnabled ? deriveProducedJobStatus(job) : deriveJobStatus(job.jobExecutionStatus));

export type ScheduleEventGapFields = Pick<
	ProductionScheduleSsrmRow,
	"noMaterialEvents" | "noRecipeAtSite" | "noTaskEvents"
>;

export const scheduleEventGapCodes = ({
	noMaterialEvents,
	noRecipeAtSite,
	noTaskEvents
}: ScheduleEventGapFields): MissingDataCode[] => [
	...(noTaskEvents === true ? [MissingDataCode.ProductionJobNoTaskEvents] : []),
	...(noMaterialEvents === true ? [MissingDataCode.ProductionJobNoMaterialEvents] : []),
	...(noRecipeAtSite === true ? [MissingDataCode.ProductionJobNoRecipeAtSite] : [])
];

export const POSTING_STATUS_LABELS: Record<JobPostingStatus, string> = {
	[JobPostingStatus.Closed]: "Closed",
	[JobPostingStatus.NotPosted]: "Not posted",
	[JobPostingStatus.PartiallyPosted]: "Partially posted"
};

export const postingStatusLabel = (status: JobPostingStatus | null | undefined): string =>
	(status && POSTING_STATUS_LABELS[status]) ?? POSTING_STATUS_LABELS[JobPostingStatus.NotPosted];

export const isJobReopenable = (status: JobPostingStatus | null | undefined): boolean =>
	status === JobPostingStatus.PartiallyPosted || status === JobPostingStatus.Closed;

// Adjustable once posted; wsapi allows adjusting a Closed job without reopening (it flags AppliedWhileClosed).
export const isJobAdjustable = (status: JobPostingStatus | null | undefined): boolean =>
	status === JobPostingStatus.PartiallyPosted || status === JobPostingStatus.Closed;

const UNSTAMPED_ISO_WEEK = 0;

export const originalWeekNumber = (isoWeek: number | null | undefined): number | null =>
	isoWeek == null || isoWeek <= UNSTAMPED_ISO_WEEK ? null : isoWeek;

export const ORIGINAL_WEEK_COLUMNS_HIDDEN_BY_DEFAULT = true;
